import { RouterRootState } from "connected-react-router";
import  { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useDialog } from "../../../components/dialog/DialogContext";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import { Topic } from "../../../components/item-content-two/Topic";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { ExpiryReason as ExpiryReasonModel } from "@/models/ExpiryReason";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { TabSummary } from "../../../models/TabSummary";
import { getErrorMessage, showLoadItemError } from "../../../services/utilities/ServiceError";
import {  getExpiryReasonById, getExpiryReasonNamesWithPagination, saveExpiryReason } from "@/services/expiryReasonService";
import ExpiryReasonCard from "@/components/expiry-reasons/ExpiryReasonCard";
import { MultiSelectOption } from "@/models/MultiSelectOption";
import ExpiryReasonsMembersTab from "@/components/expiry-reasons/ExpiryReasonsMembersTab";

function getIdFromPath(path: string) {
    const splitPath = path.split("/");
    return splitPath[splitPath.length - 1];
}

const itemPath = "/access-control/configuration/compliance-condition/";
const allItemsPath = "/access-control/configuration/operational-compliance";

const ExpiryReason: FC = () => {
    const initialItem: ExpiryReasonModel = {
        id: undefined as any,
        name: '',
        nameOriginal: '',
        duration: '',
        durationWarning: '',
        durationValue: 0,
        durationUnit: '',
        durationWarnValue: 0,
        eventExpiry: '',
        eventWarning: ''
    }
    const { translate } = useLocale();
    const initialTabs = [
        {
            label: translate.byKey(TranslationKeys.members), badge: 0, showInitial: true, isActive: false,
        },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState<string>(pageId);
    const [currentItem, setCurrentItem] = useState<ExpiryReasonModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const reloadPage = async () => {
        try {
            let expiryReason: any;
            if (itemId) {
                expiryReason = await getExpiryReasonById(itemId);
                setSelectedItem({ label: expiryReason.name, value: expiryReason.id.toString(), isDefault: expiryReason.isDefault });
                setCurrentItem(expiryReason);
                setTabs(updateTabBadgeTotal(0, expiryReason?.totalMasterExpiries, initialTabs));
            } else {
                setCurrentItem(initialItem);
                setTabs([]);
            }
            setBreadcrumb([
                { value: "/access-control/configuration", displayName: translate.byKey(TranslationKeys.configuration)},
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.operationalCompliance)},
                { value: itemPath + `${itemId}`, displayName: `${expiryReason?.name ?? ""}`,},
            ]);
            
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath); });
        }
    };

    useEffect(() => {
        setItemId(pageId);
    }, [pageId]);

    useEffect(() => {
        reloadPage();
    }, [itemId]);

    const tabContentList: ContentWrapper[] = [
        {
            content: <ExpiryReasonsMembersTab itemId={itemId} setBusy={setBusy} currentItem={currentItem} dataReloadMethod={reloadPage}/>
        },
    ];

    const saveItem = async (values: ExpiryReasonModel) => {
        const expiryReason: ExpiryReasonModel = {
            id: values.id ?? 0,
            name: values.name,
            nameOriginal: values.nameOriginal,
            duration: values.duration,
            durationValue: values.durationValue,
            durationWarning: values.durationWarning,
            durationUnit: values.durationUnit,
            durationWarnValue: values.durationWarnValue,
            eventExpiry: values.eventExpiry,
            eventWarning: values.eventWarning
        }

        const createNew = values.id ? false : true;

        setBusy(true);
        saveExpiryReason(expiryReason, createNew).then(async(value: any) => {
            setCurrentItem(expiryReason)
            setItemId(value.id)
            history.push(`${itemPath}${value.id}`);
            toast.success(translate.byKey(TranslationKeys.success))
        })
        .catch(error => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        })
        .finally(() => {
            setBusy(false);
        });
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; 
        return newTabs;
    };

    const getTabContent = (index?: number) => {
        if (tabs[index ?? 0].isActive && itemId) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
    };

    const topic: Topic = {
        accessor: "",
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.complianceCondition),
    };

    return  <div className="flex h-full">
        <TopicContextProvider dataMethod={getExpiryReasonNamesWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem}>
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveItem}
                    tabs={tabs}
                    initialValues={currentItem}>
                    <ExpiryReasonCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </div>
};

export default ExpiryReason
